// Normalizes awkward streamed markdown like:
// - `File.ts` - Description
// and paragraphs that are solely `` `- Description` `` so they render
// as proper list structures. Implemented as a remark plugin.

// Exported as a plugin function for remark: it returns a transformer
// that mutates the AST in-place.
export default function normalizeContent() {
  const isCodeHyphenParagraph = (node: any) => {
    if (!node || node.type !== 'paragraph' || !Array.isArray(node.children) || node.children.length !== 1) return false;
    const only = node.children[0];
    return only && only.type === 'inlineCode' && typeof only.value === 'string' && /^-\s+/.test(only.value);
  };

  const extractText = (node: any) => {
    const only = node.children[0];
    const value = String(only.value || '');
    return value.replace(/^-[\t ]+/, '');
  };

  const makeListFromParas = (paras: any[]) => {
    return {
      type: 'list',
      ordered: false,
      spread: false,
      children: paras.map((p) => ({
        type: 'listItem',
        spread: false,
        children: [
          {
            type: 'paragraph',
            children: [{ type: 'text', value: extractText(p) }]
          }
        ]
      }))
    } as any;
  };

  const transformListItem = (item: any) => {
    if (!item || !Array.isArray(item.children)) return;
    const newChildren: any[] = [];
    let buffer: any[] = [];
    const flush = () => {
      if (buffer.length > 0) {
        newChildren.push(makeListFromParas(buffer));
        buffer = [];
      }
    };
    for (const child of item.children) {
      if (isCodeHyphenParagraph(child)) {
        buffer.push(child);
        continue;
      }

      // Split single-line pattern: paragraph children like [inlineCode, text(' - description...')]
      if (child && child.type === 'paragraph' && Array.isArray(child.children) && child.children.length >= 2) {
        let idxCode = -1;
        for (let i = 0; i < child.children.length; i++) {
          if (child.children[i].type === 'inlineCode') { idxCode = i; break; }
        }
        if (idxCode >= 0 && child.children[idxCode + 1] && child.children[idxCode + 1].type === 'text') {
          const followingText = String(child.children[idxCode + 1].value || '');
          const m = followingText.match(/^\s*-\s+([\s\S]*)/);
          if (m) {
            let desc = m[1];
            if (child.children.length > idxCode + 2) {
              const tail = child.children.slice(idxCode + 2)
                .map((n: any) => (n.type === 'text' || n.type === 'inlineCode') ? String(n.value || '') : '')
                .join('');
              desc += tail;
            }
            const headParagraph = {
              type: 'paragraph',
              children: [ { ...child.children[idxCode] } ]
            };
            const nested = makeListFromParas([
              { type: 'paragraph', children: [{ type: 'text', value: desc.trim() }] }
            ]);
            flush();
            newChildren.push(headParagraph);
            newChildren.push(nested);
            continue;
          }
        }
      }

      flush();
      newChildren.push(child);
    }
    flush();
    item.children = newChildren;
  };

  const visitNode = (node: any) => {
    if (!node || !node.type) return;
    if (node.type === 'list' && Array.isArray(node.children)) {
      for (const li of node.children) {
        transformListItem(li);
      }
    }
    if (Array.isArray((node as any).children)) {
      const kids = (node as any).children;
      const rebuilt: any[] = [];
      let buffer: any[] = [];
      const flush = () => {
        if (buffer.length > 0) {
          rebuilt.push(makeListFromParas(buffer));
          buffer = [];
        }
      };
      for (const child of kids) {
        if (isCodeHyphenParagraph(child)) {
          buffer.push(child);
        } else {
          flush();
          rebuilt.push(child);
        }
      }
      flush();
      (node as any).children = rebuilt;
      for (const ch of (node as any).children) visitNode(ch);
    }
  };

  return (tree: any) => {
    try {
      visitNode(tree);
    } catch (_e) {
      // fail-safe: no-op on error
    }
  };
}


